import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCall } from '../context/CallContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Video, Phone, Circle, Users, Search, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

export default function VideoCallPage() {
  const { user } = useAuth();
  const { startCall, callState, wsConnected } = useCall();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [onlineRes, membersRes] = await Promise.all([
          api.get('/call/online-users'),
          api.get('/call/members'),
        ]);
        setUsers(onlineRes.data.users || []);
        setAllUsers((membersRes.data.members || []).filter(m => m.id !== user?.id));
      } catch {}
      setLoading(false);
    };
    load();
    // Poll more aggressively so newly-connected members show up quickly
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // A user is considered online if the backend said so, or if they're currently
  // connected to the call WebSocket (our own WS) — whichever is more optimistic.
  const onlineIds = new Set(users.map(u => u.id));
  // The current device itself is always online (since we're the one rendering the page)
  if (user?.id) onlineIds.add(user.id);
  const filtered = allUsers.filter(u =>
    u.id !== user?.id && (
      !search || (u.display_name || u.username || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const handleCall = async (target, type) => {
    if (callState !== 'idle') { toast.error('Already in a call'); return; }
    if (!wsConnected) { toast.error('Connecting to call server...'); return; }

    // ── Pre-flight permission check ──
    // Trigger navigator.mediaDevices.getUserMedia BEFORE call initialization so the OS
    // permission prompt appears immediately. If the user denies or the device is missing,
    // we surface a clear toast instead of the call silently failing mid-signaling.
    const constraints = type === 'video'
      ? { video: true, audio: true }
      : { audio: true, video: false };

    let preflightStream;
    try {
      preflightStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error(`Permission denied. Please allow ${type === 'video' ? 'camera and microphone' : 'microphone'} access in your browser settings.`);
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast.error(type === 'video' ? 'No camera or microphone found on this device.' : 'No microphone found on this device.');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        toast.error('Your microphone/camera is in use by another app. Please close it and retry.');
      } else if (name === 'OverconstrainedError') {
        toast.error('Your device does not meet the call requirements.');
      } else {
        toast.error('Could not access microphone/camera: ' + (err?.message || name || 'unknown error'));
      }
      return;
    }
    // Release the pre-flight stream — startCall() will acquire a fresh one.
    preflightStream.getTracks().forEach(t => t.stop());

    startCall(target.id, target.display_name || target.username, type);
  };

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="video-call-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-gold/10 flex items-center justify-center rounded-xl">
          <Video size={18} className="text-gold" />
        </div>
        <div>
          <h1 className="font-serif text-2xl md:text-3xl">Video & Audio Calls</h1>
          <p className="text-sm text-muted-foreground">Call any member directly — like WhatsApp</p>
        </div>
      </div>

      {/* Connection status */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs mt-4 mb-4 ${wsConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="ws-status">
        {wsConnected ? <><Wifi size={12} /><span>Connected — ready to make and receive calls</span></> : <><WifiOff size={12} /><span>Connecting to call server...</span></>}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search members..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-border/40 rounded-xl bg-white focus:outline-none focus:border-amber-300"
          data-testid="user-search"
        />
      </div>

      {/* Online users */}
      {users.filter(u => u.id !== user?.id).length > 0 && (
        <div className="mb-6">
          <h2 className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium mb-2 flex items-center gap-1.5">
            <Circle size={6} className="fill-emerald-500 text-emerald-500" /> Online Now
          </h2>
          <div className="space-y-1">
            {users.filter(u => u.id !== user?.id).map(u => (
              <UserRow key={u.id} user={u} online onCall={handleCall} callState={callState} />
            ))}
          </div>
        </div>
      )}

      {/* All members */}
      <div>
        <h2 className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
          <Users size={10} /> All Members
        </h2>
        {loading ? (
          <p className="text-xs text-muted-foreground py-8 text-center">Loading members...</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground py-8 text-center">No members found</p>
        ) : (
          <div className="space-y-1">
            {filtered.map(u => (
              <UserRow key={u.id} user={u} online={onlineIds.has(u.id)} onCall={handleCall} callState={callState} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserRow({ user: u, online, onCall, callState }) {
  const initial = (u.display_name || u.username || 'U')[0].toUpperCase();
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors" data-testid={`user-row-${u.id}`}>
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center border border-border/30">
          <span className="text-sm font-serif text-obsidian">{initial}</span>
        </div>
        {online && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-obsidian truncate">{u.display_name || u.username}</p>
        <p className="text-[10px] text-muted-foreground">{online ? 'Online' : 'Offline'}</p>
      </div>
      <div className="flex gap-1.5">
        <button
          onClick={() => onCall(u, 'audio')}
          disabled={callState !== 'idle'}
          className="w-9 h-9 rounded-full bg-stone-100 hover:bg-emerald-100 text-stone-500 hover:text-emerald-600 flex items-center justify-center transition-colors disabled:opacity-30"
          title="Audio call"
          data-testid={`audio-call-${u.id}`}
        >
          <Phone size={14} />
        </button>
        <button
          onClick={() => onCall(u, 'video')}
          disabled={callState !== 'idle'}
          className="w-9 h-9 rounded-full bg-stone-100 hover:bg-blue-100 text-stone-500 hover:text-blue-600 flex items-center justify-center transition-colors disabled:opacity-30"
          title="Video call"
          data-testid={`video-call-${u.id}`}
        >
          <Video size={14} />
        </button>
      </div>
    </div>
  );
}
